import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, CaretDown, CaretRight, MagnifyingGlass, FileText, X, Plus, ArrowClockwise, Package,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { devApi } from './devApi';
import { CitationList, ConfidenceBadge } from './Citations';

const DMS = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

/**
 * AI 必要部品アシスタント — 構想提案・図面・回路図・写真（OCR）・メモから必要部品を提案する。
 * 自動保存は一切しない。出典（書類・ページ・スニペット）を必ず表示する。
 */
export const PartsAiSuggest = ({ devId, linkedDocs, canEdit, buildQty, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [useLinked, setUseLinked] = useState(true);
  const [notes, setNotes] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sel, setSel] = useState({});

  const search = async (v) => {
    setQ(v);
    if (!v.trim()) { setHits([]); return; }
    try {
      const r = await axios.get(`${DMS}/documents`, { params: { q: v, limit: 8 } });
      setHits(r.data || []);
    } catch (_e) { setHits([]); }
  };

  const suggest = async () => {
    setBusy(true); setSel({});
    try {
      const r = await devApi.aiSuggestParts(devId, {
        notes, document_ids: picked.map((p) => p.id), include_linked: useLinked,
      });
      setResult(r);
      if (r.note) toast.error(r.note);
      const next = {};
      (r.parts || []).forEach((p, i) => { if (!p.already_added) next[i] = true; });
      setSel(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AIの部品提案を生成できませんでした');
    }
    setBusy(false);
  };

  const addSelected = async () => {
    const rows = (result?.parts || []).filter((_p, i) => sel[i]);
    if (rows.length === 0) { toast.error('追加する部品を選んでください'); return; }
    try {
      await devApi.addParts(devId, rows.map((p) => ({
        source: p.suggested_source,
        item_id: p.match?.item_id || null,
        name: p.name,
        part_no: p.part_no || null,
        drawing_no: p.drawing_no || null,
        drawing_revision: p.drawing_revision || null,
        maker: p.maker || null,
        maker_pn: p.maker_pn || null,
        model_no: p.model_no || null,
        uom: p.uom || '個',
        qty_per_unit: p.qty_per_unit,
        ai_suggested: true,
        ai_confidence: p.confidence,
        citations: p.citations || [],
      })));
      toast.success(`${rows.length}件の部品を必要部品に追加しました`);
      setResult(null); setSel({});
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || '追加できませんでした');
    }
  };

  const selectedCount = Object.values(sel).filter(Boolean).length;

  return (
    <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-parts-ai">
      <button type="button" onClick={() => setOpen(!open)} data-testid="dev-parts-ai-toggle"
        className="flex w-full items-center gap-2 border-b border-zinc-200 bg-zinc-100 px-4 py-2.5 text-left hover:bg-zinc-200/60">
        {open ? <CaretDown size={14} className="text-zinc-600" /> : <CaretRight size={14} className="text-zinc-600" />}
        <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
          <Sparkle size={15} weight="duotone" className="text-teal-700" /> AIアシスタント — 必要部品を提案
        </p>
        <p className="hidden text-xs text-zinc-600 sm:block">
          構想提案・図面・回路図・写真・メモから必要部品を出典付きで提案します。自動保存はされません。
        </p>
      </button>

      {open && (
        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-800">
              <Checkbox checked={useLinked} onCheckedChange={(v) => setUseLinked(!!v)}
                data-testid="dev-parts-ai-use-linked" />
              この案件の書類（提案書・図面・フローチャート・写真ほか）を材料にする（{linkedDocs.length}件）
            </label>
            {useLinked && linkedDocs.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {linkedDocs.map((d) => (
                  <span key={d.document_id}
                    className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-zinc-50 px-2 py-1 text-xs text-zinc-800">
                    <FileText size={12} /> {d.title}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">追加で参照する書類（任意）</Label>
            <div className="relative w-64">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
              <Input value={q} onChange={(e) => search(e.target.value)} className="pl-8"
                placeholder="ドキュメントアプリから探す" data-testid="dev-parts-ai-doc-search" />
            </div>
            {hits.length > 0 && (
              <div className="mt-2 max-h-44 divide-y divide-zinc-200 overflow-y-auto rounded-md border border-zinc-300">
                {hits.map((d) => (
                  <button key={d.id} type="button"
                    onClick={() => { if (!picked.some((p) => p.id === d.id)) setPicked([...picked, d]); setQ(''); setHits([]); }}
                    data-testid={`dev-parts-ai-doc-option-${d.id}`}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-teal-50/60">
                    <FileText size={14} className="shrink-0 text-zinc-500" />
                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-900">
                      {d.document_number ? `${d.document_number} · ` : ''}{d.title}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {picked.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dev-parts-ai-picked">
                {picked.map((d) => (
                  <span key={d.id}
                    className="inline-flex items-center gap-1 rounded-md border border-teal-300 bg-teal-50 px-2 py-1 text-xs font-medium text-teal-900">
                    <FileText size={12} /> {d.title}
                    <button type="button" onClick={() => setPicked(picked.filter((p) => p.id !== d.id))}
                      aria-label="外す" className="text-teal-700 hover:text-rose-600"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">メモ（任意）</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="例: ヒーターは300W、制御はArduino DUEを使う想定。温度センサーは2点測定。"
              data-testid="dev-parts-ai-notes" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={suggest} disabled={busy || !canEdit || !buildQty}
              data-testid="dev-parts-ai-suggest-btn">
              <Sparkle size={14} className="mr-1" /> {busy ? '提案中…' : '必要部品を提案してもらう'}
            </Button>
            {result && (
              <Button variant="outline" onClick={suggest} disabled={busy}
                data-testid="dev-parts-ai-again-btn">
                <ArrowClockwise size={14} className="mr-1" /> もう一度
              </Button>
            )}
            {!buildQty && (
              <span className="text-xs font-semibold text-amber-800">
                先に製作台数を入力してください。
              </span>
            )}
          </div>

          {result && (
            <div className="space-y-3 rounded-lg border border-teal-300 bg-teal-50/40 p-4"
              data-testid="dev-parts-ai-result">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-bold text-zinc-900">
                  AIの提案（{(result.parts || []).length}件）
                </p>
                <p className="text-xs font-semibold text-teal-900">{result.disclaimer}</p>
              </div>
              {(result.sources || []).length > 0 && (
                <p className="text-xs text-zinc-700" data-testid="dev-parts-ai-sources">
                  参照した書類: {result.sources.map((s) => `${s.title}（${s.used ? `${s.chars}文字` : '文字なし'}）`).join('、')}
                </p>
              )}
              {result.note && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
                  data-testid="dev-parts-ai-note">{result.note}</p>
              )}
              {!result.note && (result.parts || []).length === 0 && (
                <p className="text-sm text-zinc-700" data-testid="dev-parts-ai-empty">
                  資料から根拠のある部品を読み取れませんでした。メモを追記してもう一度お試しください。
                </p>
              )}
              {(result.parts || []).map((p, i) => (
                <div key={`${p.name}-${i}`} className="rounded-md border border-zinc-300 bg-white p-3"
                  data-testid={`dev-parts-ai-item-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Checkbox checked={!!sel[i]} onCheckedChange={(v) => setSel({ ...sel, [i]: !!v })}
                      data-testid={`dev-parts-ai-select-${i}`} />
                    <p className="text-sm font-bold text-zinc-900">{p.name}</p>
                    {p.model_no && <span className="text-xs text-zinc-600">{p.model_no}</span>}
                    <ConfidenceBadge level={p.confidence} testid={`dev-parts-ai-conf-${i}`} />
                    <StatusBadge tone={p.match ? 'info' : 'neutral'} testid={`dev-parts-ai-source-${i}`}>
                      <Package size={11} /> {p.suggested_source_label}
                    </StatusBadge>
                    {p.already_added && (
                      <StatusBadge tone="success" testid={`dev-parts-ai-added-${i}`}>登録済み</StatusBadge>
                    )}
                    <span className="ml-auto text-xs font-semibold tabular-nums text-zinc-800">
                      {p.qty_per_unit}{p.uom} / 台 × {result.build_quantity}台 = {p.required_total}{p.uom}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-zinc-600">
                    {p.maker && <span>メーカー: {p.maker}</span>}
                    {p.maker_pn && <span>メーカー品番: {p.maker_pn}</span>}
                    {p.drawing_no && (
                      <span>図番: {p.drawing_no}{p.drawing_revision ? ` ${p.drawing_revision}` : ''}</span>
                    )}
                    {p.match && <span>マスター候補: {p.match.name}（{p.match.sku || '—'}）</span>}
                  </div>
                  {p.reason && <p className="mt-1 text-xs text-zinc-600">理由: {p.reason}</p>}
                  <CitationList citations={p.citations} testid={`dev-parts-ai-citations-${i}`} />
                </div>
              ))}
              {(result.parts || []).length > 0 && (
                <Button onClick={addSelected} disabled={!canEdit || selectedCount === 0}
                  data-testid="dev-parts-ai-add-btn">
                  <Plus size={14} className="mr-1" /> 選んだ部品を必要部品に追加（{selectedCount}件）
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
